import axios from "axios";
const axiosErrorToLogMap = {
    http: {
        url: "config.url",
        status_code: "response.status",
        method: "request.method"
    }
};
const LOG_STATUS = {
    INFO: "info",
    WARN: "warn",
    ERROR: "error"
};
/**
 * Formats the message key for the log e.g. HTTP GET /foo/bar
 * @example
 * const message = formatMessage("GET", "foo/bar");
 * console.log(message);
 */
const formatMessage = (method, url) => `HTTP ${method} ${url}`;
/**
 * Returns the log level string based on the status code
 * @example
 * const logLevel = getLogLevel(500);
 * console.log(logLevel);
 */
const getLogLevel = (statusCode = 200, defaultLevel = LOG_STATUS.INFO) => {
    switch (Math.floor(Number(statusCode) / 100)) {
        case 5:
            return LOG_STATUS.ERROR;
        case 4:
            return LOG_STATUS.WARN;
        default:
            return defaultLevel;
    }
};
/**
 * Gets a nested value from on object via a string reference
 * @example
 * const nestedObject = { foo: { bar: 7 } };
 * const nestedValue = getNested(nestedObject, "foo.bar");
 * console.log(nestedValue);
 */
const getNested = (theObject, path, separator = ".") => {
    try {
        const normalizedPath = path.replace(/\[(\w+)\]/g, (match, p) => `${separator}${p}`);
        const keys = normalizedPath.split(separator);
        return keys.reduce((obj, property) => {
            if (obj && typeof obj === "object" && property in obj) {
                return obj[property];
            }
            else {
                // Returning undefined to break out of the reduce if the path is not valid
                return undefined;
            }
        }, theObject);
    }
    catch (err) {
        return undefined;
    }
};
/**
 * Creates a new object using the values in mappedObject and the
 * key references in the map. Essentially an object reducer.
 * @example
 * const mappableObject = { foo: { bar: 7 } };
 * const map = { baz: "foo.bar" };
 * const mappedObject = mappedObject(mappableObject, map);
 * console.log(mappedObject);
 */
const mapObj = (mappedObject, map) => {
    const newObject = {};
    // Traverse the object
    Object.keys(map).forEach((key) => {
        let newObjectValue;
        if (typeof map[key] === "object") {
            newObjectValue = mapObj(mappedObject, map[key]);
        }
        else {
            newObjectValue = getNested(Object.assign({}, mappedObject), map[key]);
        }
        newObject[key] = newObjectValue;
    });
    return newObject;
};
/**
 * Uses values from a Koa context object and an optional error object
 * to create a formatted log object. Uses mapObj to translate to the
 * desired format. Takes an optional mapping argument to override the default.
 * @example
 * const formattedLog = formatLog(context, error);
 * console.log(formattedLog);
 */
export const formatLog = (format) => {
    var _a;
    const { context, error, contextToLogMap = axiosErrorToLogMap } = format;
    let contextValues = null;
    let logInfo = null;
    if (axios.isAxiosError(error)) {
        contextValues = mapObj(Object.assign({}, error), Object.assign({}, contextToLogMap));
        logInfo = Object.assign(Object.assign({}, contextValues), { level: getLogLevel(((_a = contextValues === null || contextValues === void 0 ? void 0 : contextValues.http) === null || _a === void 0 ? void 0 : _a.status_code) || error.status), message: formatMessage(contextValues.http.method || error.message, contextValues.http.url) });
    }
    else {
        const { req: { method = "", url = "" } = {}, res: { statusCode = 0 } = {} } = context || {};
        // @ts-ignore: TS2352 type conversion error
        contextValues = mapObj(Object.assign({}, context), Object.assign({}, contextToLogMap));
        logInfo = Object.assign(Object.assign({}, contextValues), { level: getLogLevel(statusCode), message: formatMessage(method, url) });
    }
    const errorValues = error
        ? {
            message: error.message,
            stack: error.stack,
            kind: error.name
        }
        : null;
    return Object.assign(Object.assign({}, logInfo), (errorValues && { error: errorValues }));
};
//# sourceMappingURL=logging.js.map